import { useCallback, useEffect, useRef, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import ReactPlayer from 'react-player/lazy';
import screenfull from 'screenfull';
import { detectSystemMobile } from 'utils';
import { ESystemMobile } from 'types';

type Props = {
  className?: string;
  url: string;
  isFullscreenOnclick?: boolean;
  isAutoPlayOnView?: boolean;
  controls?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  onLoad?: () => void;
  poster?: string;
  type: string;
  topOffset?: string | number;
  bottomOffset?: string | number;
  scrollableAncestor?: HTMLElement | null;
};

function VideoPlayer({
  className,
  url,
  isFullscreenOnclick = false,
  isAutoPlayOnView = false,
  controls = true,
  autoPlay = true,
  loop = true,
  onLoad,
  poster,
  type,
  topOffset,
  bottomOffset,
  scrollableAncestor: propScrollableAncestor,
}: Props) {
  const playerRef = useRef<any>(null);
  const videoRef = useRef<any>(null);

  const timeoutRef = useRef<NodeJS.Timeout>();
  const intervalRef = useRef<NodeJS.Timeout>();

  // 手動再生モードで初回だけミュート解除するためのフラグ。
  // 2 回目以降の再開はユーザーのミュート操作（ネイティブコントロール）を尊重する
  const hasUnmutedRef = useRef(false);

  const [isPlaying, setIsPlaying] = useState<boolean>(false);

  const scrollableAncestor = propScrollableAncestor || undefined;

  const onPlayFullscreen = () => {
    if (!timeoutRef.current) {
      // Waiting for video to be ready to play
      timeoutRef.current = setTimeout(async () => {
        try {
          const player = playerRef.current?.player?.player;
          if (!player) return;

          setIsPlaying(true);
          player.unmute();

          await new Promise((resolve, _reject) => {
            // Check until the video starts playing, enter full screen mode.
            intervalRef.current = setInterval(() => {
              if (videoRef.current?.webkitEnterFullscreen) {
                videoRef.current.webkitEnterFullscreen();
                resolve(true);
              } else if (videoRef.current?.requestFullscreen) {
                videoRef.current.requestFullscreen();
                resolve(true);
              }
            }, 300);
          });
        } catch (error) {
        } finally {
          clearTimer();
        }
      }, 500);
    }
  };

  const clearTimer = useCallback(() => {
    clearTimeout(timeoutRef.current);
    clearInterval(intervalRef.current);
    timeoutRef.current = undefined;
    intervalRef.current = undefined;
  }, []);

  const handleFullscreenEvent = useCallback(() => {
    const player = playerRef.current.player?.player;
    if (player) {
      if (screenfull.isFullscreen) {
        player.unmute();
      } else if (isAutoPlayOnView) {
        // 自動再生（無音ループ）に戻るときだけミュートする。
        // 手動再生モードでは全画面解除後もインラインで音声つき再生を続ける
        player.mute();
      }
    }
  }, [isAutoPlayOnView]);

  const onEnter = useCallback(() => {
    if (isAutoPlayOnView) {
      setIsPlaying(true);
    }
  }, [isAutoPlayOnView, setIsPlaying]);

  const onLeave = useCallback(() => {
    setIsPlaying(false);
    // 自動再生（無音ループ）へ戻すときだけミュートする。
    // 手動再生モードは pause のみで、ユーザーのミュート状態を保持する
    if (isAutoPlayOnView) {
      playerRef.current.player?.player.mute();
    }
    clearTimer();
  }, [isAutoPlayOnView, setIsPlaying, clearTimer]);

  // video 要素が取れたことを effect 側へ伝えるためのフラグ
  const [isVideoElementReady, setIsVideoElementReady] = useState(false);

  const handleOnReady = useCallback(() => {
    if (videoRef.current) return;
    onLoad && onLoad();
    videoRef.current = playerRef.current.player?.player?.player;
    setIsVideoElementReady(!!videoRef.current);
  }, [onLoad]);

  // 登録と解除を同じ effect に置く。handleFullscreenEvent は isAutoPlayOnView に
  // 依存するため、分離すると props 変更時に解除だけ走ってリスナーが失われる
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    videoElement.addEventListener('fullscreenchange', handleFullscreenEvent);
    return () => {
      videoElement.removeEventListener(
        'fullscreenchange',
        handleFullscreenEvent
      );
    };
  }, [handleFullscreenEvent, isVideoElementReady]);

  return (
    <div className="relative h-full">
      {isFullscreenOnclick && (
        <div
          data-testid="fullscreen-overlay"
          className="absolute w-full h-full bg-transparent z-[1]"
          onClick={onPlayFullscreen}
        ></div>
      )}
      <Waypoint
        onEnter={onEnter}
        onLeave={onLeave}
        topOffset={topOffset}
        bottomOffset={bottomOffset}
        scrollableAncestor={scrollableAncestor}
      >
        <div className="h-full">
          <ReactPlayer
            ref={playerRef}
            playing={isPlaying}
            loop={loop}
            controls={controls}
            muted
            autoPlay={autoPlay && !isAutoPlayOnView}
            playsinline
            light={
              poster && !controls ? (
                <div
                  className={`w-full h-full z-[2] absolute flex justify-center items-center cursor-pointer bg-no-repeat bg-contain bg-center`}
                  onClick={onPlayFullscreen}
                  style={{ backgroundImage: `url('${poster}')` }}
                >
                  <div className="w-full h-full bg-black/10 absolute"></div>
                  <div className="w-1/3 aspect-square rounded-full border-[4px] border-solid border-white relative z-[2]"></div>
                  <div className="w-0 h-0 ml-1 absolute border-t-[14px] border-t-transparent border-l-[26px] border-l-white border-b-[14px] border-b-transparent"></div>
                </div>
              ) : undefined
            }
            config={{
              file: {
                attributes: {
                  controlsList: 'nodownload',
                  className,
                },
              },
            }}
            url={[
              {
                src: url,
                // TODO: to make quicktime videos work well on iPhone
                type:
                  detectSystemMobile() === ESystemMobile.IOS ? type : undefined,
              },
            ]}
            width={'100%'}
            height={'100%'}
            onReady={handleOnReady}
            onPause={() => {
              setIsPlaying(false);
            }}
            onPlay={() => {
              setIsPlaying(true);
              // 手動再生は初回だけミュート解除する。以降の再開はユーザーの
              // ミュート操作を尊重して上書きしない
              if (!isAutoPlayOnView && !hasUnmutedRef.current) {
                hasUnmutedRef.current = true;
                playerRef.current?.player?.player?.unmute();
              }
            }}
          />
        </div>
      </Waypoint>
    </div>
  );
}

export default VideoPlayer;
